import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { Plus, BookOpen, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';

interface Course {
  id: string;
  title: string;
  description: string;
  price: number;
  thumbnail_url: string | null;
  is_published: boolean;
  created_at: string;
}

export default function MyCoursesPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishingId, setPublishingId] = useState<string | null>(null);

  useEffect(() => {
    if (profile?.role !== 'docente') {
      navigate('/dashboard');
    } else {
      fetchMyCourses();
    }
  }, [profile, navigate]);

  const fetchMyCourses = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('teacher_id', profile?.id)
      .order('created_at', { ascending: false });

    if (data) {
      setCourses(data);
    }
    setLoading(false);
  };

  const togglePublish = async (courseId: string, currentStatus: boolean) => {
    setPublishingId(courseId);

    const { error } = await supabase
      .from('courses')
      .update({ is_published: !currentStatus })
      .eq('id', courseId);

    if (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile modificare lo stato del corso',
        variant: 'destructive',
      });
    } else {
      toast({
        title: currentStatus ? 'Corso rimosso' : 'Corso pubblicato',
        description: currentStatus
          ? 'Il corso non è più visibile agli studenti'
          : 'Il corso è ora visibile nel calendario agli studenti',
      });
      fetchMyCourses();
    }

    setPublishingId(null);
  };

  if (profile?.role !== 'docente') {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              I miei corsi
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Gestisci i tuoi corsi e contenuti
            </p>
          </div>
          <Link to="/dashboard/create-course">
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Crea nuovo corso
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-80" />
            ))}
          </div>
        ) : courses.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <BookOpen className="w-12 h-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nessun corso creato
              </h3>
              <p className="text-gray-600 text-center mb-4">
                Inizia a creare il tuo primo corso
              </p>
              <Link to="/dashboard/create-course">
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Crea corso
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {courses.map((course) => (
              <Card key={course.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-video bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                  {course.thumbnail_url ? (
                    <img
                      src={course.thumbnail_url}
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <BookOpen className="w-16 h-16 text-white opacity-50" />
                  )}
                </div>
                <CardHeader>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <CardTitle className="text-lg line-clamp-2">
                      {course.title}
                    </CardTitle>
                    <Badge variant={course.is_published ? 'default' : 'secondary'}>
                      {course.is_published ? 'Pubblicato' : 'Bozza'}
                    </Badge>
                  </div>
                  <CardDescription className="line-clamp-2">
                    {course.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-600">
                      Creato il {new Date(course.created_at).toLocaleDateString('it-IT')}
                    </span>
                    <span className="text-lg font-bold text-blue-600">
                      €{course.price}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <Button
                      onClick={() => togglePublish(course.id, course.is_published)}
                      disabled={publishingId === course.id}
                      variant={course.is_published ? 'outline' : 'default'}
                      className="w-full"
                      size="sm"
                    >
                      {publishingId === course.id ? (
                        'Caricamento...'
                      ) : course.is_published ? (
                        <>
                          <EyeOff className="w-4 h-4 mr-2" />
                          Rimuovi pubblicazione
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          Pubblica corso
                        </>
                      )}
                    </Button>
                    <div className="flex gap-2">
                      <Link to={`/dashboard/my-courses/${course.id}/edit`} className="flex-1">
                        <Button variant="outline" className="w-full" size="sm">
                          <Edit className="w-4 h-4 mr-2" />
                          Modifica
                        </Button>
                      </Link>
                      <Button variant="ghost" size="sm">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
